import React, { useState } from 'react'
import { RiErrorWarningLine } from 'react-icons/ri'

import { Grid } from '@material-ui/core'
import ICancelaViagemDTO from 'modules/lotes/domain/entities/ICancelaViagem'
import IFinalizaViagemDTO from 'modules/lotes/domain/entities/IFinalizaViagem'
import { ICancelaViagem } from 'modules/lotes/domain/usecases/CancelaViagem'
import { IFinalizaViagem } from 'modules/lotes/domain/usecases/FinalizaViagem'
import { useDirecionaCarga } from 'modules/lotes/hooks/direcionaCarga'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import Modal from 'shared/components/Modal'
import Text from 'shared/components/Text'
import { colors } from 'shared/styles/colors'
import dimensions from 'shared/styles/dimensions'

import { EStatusCaminhao } from '../../SelecionaCaminhao'
import { EStatusMotorista } from '../../SelecionaMotorista'
import { StyleButton, StyledCheck } from './styles'
import { registerEvent } from 'shared/services/TrackingGAService'

type TProps = {
  openModal: boolean
  setOpenModal: () => void
  status: EStatusMotorista | EStatusCaminhao
  finalizaViagem: IFinalizaViagem
  cancelaViagem: ICancelaViagem
  loteConjuntoId: number
  contatoFilial: string
}

const ModalFinalizarCancelarViagem: React.FC<TProps> = ({
  openModal,
  setOpenModal,
  status,
  finalizaViagem,
  cancelaViagem,
  loteConjuntoId,
  contatoFilial,
}) => {
  const [openModalSucesso, setOpenModalSucesso] = useState(false)
  const [loadingBackdrop, setLoadingBackdrop] = useState(false)
  const { setMotorista, setCaminhao } = useDirecionaCarga()

  const registrarEventoFinalizarViagem = () => {
    const telaOrigem =
      status === EStatusMotorista.EMBARCADO
        ? 'Tela de seleção do Motorista'
        : 'Tela de seleção do Caminhão'
    registerEvent({
      categoria: 'Finalizar Viagem',
      acao: 'Click no botão de Finalizar Viagem',
      local: telaOrigem,
    })
  }
  const registrarEventoCancelarViagem = () => {
    const telaOrigem =
      status === EStatusMotorista.EM_TRANSITO
        ? 'Tela de seleção do Motorista'
        : 'Tela de seleção do Caminhão'
    registerEvent({
      categoria: 'Cancelar Viagem',
      acao: 'Click no botão de Cancelar Viagem',
      local: telaOrigem,
    })
  }

  const handleFinalizarViagem = () => {
    registrarEventoFinalizarViagem()
    const init = async () => {
      setLoadingBackdrop(true)
      try {
        const params: IFinalizaViagemDTO = {
          lote_conjunto_id: loteConjuntoId,
        }
        const response = await finalizaViagem.execute(params)
        setLoadingBackdrop(false)
        setOpenModalSucesso(response)
      } catch (error) {
        setLoadingBackdrop(false)
      }
    }
    init()
  }

  const handleCancelarViagem = () => {
    registrarEventoCancelarViagem()
    const init = async () => {
      setLoadingBackdrop(true)
      try {
        const params: ICancelaViagemDTO = {
          lote_conjunto_id: loteConjuntoId,
        }
        const response = await cancelaViagem.execute(params)
        setLoadingBackdrop(false)
        setOpenModalSucesso(response)
      } catch (error) {
        setLoadingBackdrop(false)
      }
    }
    init()
  }

  const handleSelecionarOutroMotorista = () => {
    setOpenModal()
    setMotorista(null)
  }

  const handleSelecionarOutroCaminhao = () => {
    setOpenModal()
    setCaminhao(null)
  }

  const handleCloseModals = () => {
    setOpenModalSucesso(false)
    setOpenModal()
  }

  const titulo = (): string => {
    const texto = ''
    switch (status) {
      case EStatusMotorista.EMBARCADO: {
        return 'Motorista em viagem'
      }
      case EStatusMotorista.EM_TRANSITO: {
        return 'Motorista vinculado em outra viagem'
      }
      case EStatusCaminhao.EMBARCADO: {
        return 'Caminhão em viagem'
      }
      case EStatusCaminhao.EM_TRANSITO: {
        return 'Caminhão vinculado em outra viagem'
      }
      default: {
        break
      }
    }
    return texto
  }

  const mensagem = (): string => {
    let tipo = ''
    if (
      status === EStatusMotorista.EMBARCADO ||
      status === EStatusMotorista.EM_TRANSITO
    ) {
      tipo = 'motorista'
    } else if (
      status === EStatusCaminhao.EMBARCADO ||
      status === EStatusCaminhao.EM_TRANSITO
    ) {
      tipo = 'caminhão'
    }

    switch (status) {
      case EStatusMotorista.EMBARCADO:
      case EStatusCaminhao.EMBARCADO:
        if (contatoFilial !== '' && contatoFilial) {
          return `O ${tipo} que você selecionou está em viagem. Para finalizar a viagem atual do ${tipo}, por gentileza, telefone para a filial através deste telefone: ${contatoFilial} ou selecione outro ${tipo} na lista.`
        } else {
          return `O ${tipo} que você selecionou está em viagem. Você pode finalizar a viagem atual do ${tipo} ou selecionar outro ${tipo} na lista, o que você deseja fazer?`
        }
      case EStatusMotorista.EM_TRANSITO:
      case EStatusCaminhao.EM_TRANSITO:
        if (contatoFilial !== '' && contatoFilial) {
          return `O ${tipo} que você selecionou está vinculado a outra viagem. Para cancelar a viagem atual do ${tipo}, por gentileza, telefone para a filial através deste telefone: ${contatoFilial} ou selecione outro ${tipo} na lista.`
        } else {
          return `O ${tipo} que você selecionou está vinculado a outra viagem. Você pode cancelar a viagem atual do ${tipo} ou selecionar outro ${tipo} na lista, o que você deseja fazer?`
        }
      default:
        return ''
    }
  }

  const isFinalizarViagem = (): boolean => {
    return (
      status === EStatusMotorista.EMBARCADO ||
      status === EStatusCaminhao.EMBARCADO
    )
  }

  const isCancelarViagem = (): boolean => {
    return (
      status === EStatusMotorista.EM_TRANSITO ||
      status === EStatusCaminhao.EM_TRANSITO
    )
  }

  return (
    <>
      <Modal
        openModal={openModal}
        size="medium"
        showCloseButton={false}
        onCloseModal={setOpenModal}
      >
        <Grid
          container
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          style={{ textAlign: 'center', height: '100%' }}
        >
          <Grid item md={12}>
            <RiErrorWarningLine size="110" color={colors.blue} />
          </Grid>

          <Grid item md={12}>
            <Text weight="700" size={dimensions?.fontSize?.largex2}>
              {titulo()}
            </Text>
          </Grid>

          <Grid item md={12}>
            <Text>{mensagem()}</Text>
          </Grid>

          <Grid container alignItems="center" justifyContent="center">
            {contatoFilial !== '' && contatoFilial ? (
              <></>
            ) : (
              <>
                {isFinalizarViagem() ? (
                  <StyleButton
                    color={colors.orange}
                    onClick={handleFinalizarViagem}
                  >
                    Finalizar viagem atual
                  </StyleButton>
                ) : (
                  <></>
                )}
                {isCancelarViagem() && (
                  <StyleButton
                    color={colors.orange}
                    onClick={handleCancelarViagem}
                  >
                    Cancelar viagem atual
                  </StyleButton>
                )}
              </>
            )}

            {status === EStatusMotorista.EMBARCADO ||
            status === EStatusMotorista.EM_TRANSITO ? (
              <StyleButton
                color={colors.green}
                onClick={handleSelecionarOutroMotorista}
              >
                Selecionar outro motorista
              </StyleButton>
            ) : (
              <StyleButton
                color={colors.green}
                onClick={handleSelecionarOutroCaminhao}
              >
                Selecionar outro caminhão
              </StyleButton>
            )}
          </Grid>
        </Grid>

        {loadingBackdrop ? (
          <LoadingBackdrop open={loadingBackdrop} color={colors.green} />
        ) : (
          <></>
        )}

        {openModalSucesso ? (
          <Modal
            openModal={openModalSucesso}
            size="medium"
            onCloseModal={handleCloseModals}
          >
            <Grid
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              style={{ textAlign: 'center', height: '80%' }}
            >
              <Grid item md={12}>
                <StyledCheck />
              </Grid>

              <Grid item md={12}>
                {status === EStatusMotorista.EMBARCADO ||
                status === EStatusCaminhao.EMBARCADO ? (
                  <Text weight="700" size={dimensions?.fontSize?.largex2}>
                    Viagem finalizada com sucesso!
                  </Text>
                ) : (
                  <></>
                )}
                {status === EStatusMotorista.EM_TRANSITO ||
                status === EStatusCaminhao.EM_TRANSITO ? (
                  <Text weight="700" size={dimensions?.fontSize?.largex2}>
                    Viagem cancelada com sucesso!
                  </Text>
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="center"
            >
              <StyleButton color={colors.orange} onClick={handleCloseModals}>
                Ok
              </StyleButton>
            </Grid>
          </Modal>
        ) : (
          <div></div>
        )}
      </Modal>
    </>
  )
}

export default ModalFinalizarCancelarViagem
